import { useEffect, useState } from 'react';
import { isSsr } from '../isSsr';

/**
 * Custom hook for media queries.
 * Monitor the document to detect when it matches (or stops matching) the given media query
 * @param {string} mediaQueryString - A string specifying the media query to parse into a MediaQueryList.
 * @param {('self' | 'parent' | 'top')} [observedWindow] - Window of he monitored document (optional).
 * @returns {boolean} matchMedia - State, which will be true if the document meets the media query's requirements.
 */
export const useMediaQuery = (
	mediaQueryString: string,
	observedWindow?: 'self' | 'parent' | 'top'
): boolean => {
	if (isSsr()) {
		// eslint-disable-next-line @typescript-eslint/ban-ts-comment
		// @ts-ignore
		return;
	}
	let targetWindow: Window;
	if (!observedWindow || observedWindow === 'self') {
		targetWindow = window;
	} else if (observedWindow === 'parent') {
		targetWindow = window.parent;
	} else if (observedWindow === 'top') {
		// eslint-disable-next-line @typescript-eslint/ban-ts-comment
		// @ts-ignore
		targetWindow = window.top;
	}

	const [matchMedia, setMatchMedia] = useState<boolean>(
		// eslint-disable-next-line @typescript-eslint/ban-ts-comment
		// @ts-ignore
		targetWindow?.matchMedia(mediaQueryString)?.matches || false
	);

	useEffect(() => {
		const mediaQueryList = targetWindow?.matchMedia(mediaQueryString);
		const mediaQueryChangeHandler = (event: MediaQueryListEvent) => {
			setMatchMedia(event.matches);
		};
		mediaQueryList?.addEventListener('change', mediaQueryChangeHandler);
		return () => {
			mediaQueryList?.removeEventListener('change', mediaQueryChangeHandler);
		};
	}, []);

	return matchMedia;
};
